import { store } from '../core/store.js';
import { refs } from '../core/refs.js';
import { applyLoadedThemeTokens, getThemeOptions, loadThemeTokens, themeLabel } from '../core/theme-tokens.js';
import { runCode, RUN_TRIGGERS } from './runner.js';
import { syncCustomSelect } from './custom-select.js';

/**
 * Front-my-End — UI Theme Switching Feature
 */
let themeServices = {
  console: null
};
let themeApplySequence = 0;
let activeThemeApplication = Promise.resolve();
let activeThemeApplicationTheme = null;

function scheduleThemeApplication(theme) {
  const sequence = ++themeApplySequence;
  activeThemeApplicationTheme = theme;
  activeThemeApplication = applyTheme(theme, sequence);
  return activeThemeApplication;
}

export function registerThemeFeature(services = {}) {
  themeServices = {
    ...themeServices,
    ...services
  };

  store.subscribe('activeTheme', (theme) => {
    if (!theme) return;
    void scheduleThemeApplication(theme);
  });
}

async function applyTheme(theme, sequence) {
  let payload = null;
  try {
    payload = await loadThemeTokens(theme);
  } catch (err) {
    console.error(`Failed to apply theme tokens for '${theme}':`, err);
  }
  if (sequence !== themeApplySequence || store.get('activeTheme') !== theme) return;
  if (payload) applyLoadedThemeTokens(payload);

  const list = document.body.classList;
  list.remove('light-mode', 'dark-mode', 'boring-light', 'boring-dark');
  list.add(theme);
  setThemeSelectValue(theme);

  themeServices.console?.system(`Theme switched to: ${themeLabel(theme)}.`);
}

export async function setTheme(theme) {
  store.set('activeTheme', theme);
  if (activeThemeApplicationTheme !== theme) {
    await scheduleThemeApplication(theme);
  } else {
    await activeThemeApplication;
  }
  // Re-compile so the sandbox receives the updated theme variables. Implicit:
  // a theme switch re-runs the last-run JS, never unrun editor JS.
  await runCode({ trigger: RUN_TRIGGERS.IMPLICIT });
}

export function setThemeSelectValue(value) {
  if (refs.menuThemeSelect) {
    refs.menuThemeSelect.value = value;
    syncCustomSelect(refs.menuThemeSelect);
  }
}

function optionNode(option) {
  return new Option(option.label, option.id);
}

function populateShareThemeChecks(options) {
  const fieldset = document.querySelector('.theme-checks');
  if (!fieldset) return;

  const legend = fieldset.querySelector('legend') || document.createElement('legend');
  legend.textContent = legend.textContent || 'Allowed themes';
  const labels = options.map((option, index) => {
    const label = document.createElement('label');
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.name = 'share-theme';
    input.value = option.id;
    input.checked = index === 0;
    label.append(input, ` ${option.label}`);
    return label;
  });
  fieldset.replaceChildren(legend, ...labels);
}

export async function populateThemeControls() {
  const options = await getThemeOptions();
  if (refs.menuThemeSelect) {
    refs.menuThemeSelect.replaceChildren(...options.map(optionNode));
  }
  populateShareThemeChecks(options);
}
